import React, { memo, useMemo, useRef, useState } from 'react';

import { Center, Flex, Text } from '@chakra-ui/react';
import { useQuery } from 'react-query';

import { useSites, useWashHubListener } from 'hooks';

import AddInventoryCountModal, {
  RefType,
} from 'components/modules/AddInventoryCountModal';
import EditInventoryCountModal, {
  EditRefType,
} from 'components/modules/EditInventoryCountModal';
import { DataTable, ServerSort } from 'components/modules/DataTable';

import { fetchAllCounts } from 'api/inventory';
import { washHub } from 'store';
import { InventoryCount } from 'types/inventory';
import { InventoryCountTabProps } from './InventoryCount.props';
import { buildInventoryCountColumns } from './inventoryCountColumns';

export const ArchivedInventory = memo<InventoryCountTabProps>((props) => {
  const {
    endDate,
    startDate,
    site,
    accountManagers,
    itemCount,
    caseCount,
  } = props;

  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<ServerSort | undefined>(undefined);
  const { data: sites } = useSites(true);
  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const addInventoryCountModalRef = useRef<RefType>(null);
  const editInventoryCountModalRef = useRef<EditRefType>(null);
  const queryKey = [
    'fetch-inventory-counts',
    page,
    washHubId,
    endDate,
    startDate,
    site?.toString(),
    accountManagers?.toString(),
    sort?.field,
    sort?.dir,
  ];

  const queryFn = () =>
    fetchAllCounts({
      isArchived: true,
      page,
      limit: 10,
      washHub: washHubId,
      endDate,
      startDate,
      site,
      ...(accountManagers && accountManagers.length ? { accountManagers } : {}),
      sortByField: sort?.field,
      sort: sort?.dir,
    });
  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };

  const { data, isFetching } = useQuery(queryKey, queryFn, options);

  useWashHubListener(() => {
    setPage(1);
  });

  const counts = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const columns = useMemo(
    () => buildInventoryCountColumns(sites, itemCount, caseCount),
    [sites, itemCount, caseCount],
  );

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <DataTable<InventoryCount>
        minW="1096px"
        data={counts}
        columns={columns}
        isLoading={isFetching}
        pagination={pagination}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(count) => {
          count.isArchived = true;
          editInventoryCountModalRef.current?.onOpen(count);
        }}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Inventory counts available</Text>
          </Center>
        )}
      />

      <AddInventoryCountModal ref={addInventoryCountModalRef} />
      <EditInventoryCountModal ref={editInventoryCountModalRef} />
    </Flex>
  );
});
